import { useRef, useState } from "react";
import type { FormComponentRef } from "@inertiajs/core";
import { Form, Head, Link } from "@inertiajs/react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Field, FieldError, FieldLabel } from "@/components/ui/field";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import SettingsLayout from "@/layouts/settings_layout";

type Member = {
  id: string;
  user_id: string;
  name: string;
  role: string;
  active: boolean;
  self: boolean;
  location_ids: string[];
};
type Location = {
  id: string;
  name: string;
  active: boolean;
  unresolved: boolean;
};
type Job = {
  id: string;
  revision: string;
  location_id: string;
  assignee_id: string;
  helper_ids: string[];
  billing_assignee_id: string | null;
  customer_name: string;
  starts_at: string;
};
type Props = {
  selection_version: string;
  selection_required: boolean;
  plan_key: string;
  plans: {
    key: string;
    name: string;
    limits: { users: number; locations: number | null };
  }[];
  members: Member[];
  locations: Location[];
  jobs: Job[];
};

function Choice({
  id,
  name,
  value,
  choices,
}: {
  id: string;
  name: string;
  value: string;
  choices: { value: string; label: string }[];
}) {
  return (
    <Select
      name={name}
      defaultValue={value}
      items={Object.fromEntries(
        choices.map((choice) => [choice.value, choice.label]),
      )}
    >
      <SelectTrigger id={id} className="w-full">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {choices.map((choice) => (
          <SelectItem key={choice.value} value={choice.value}>
            {choice.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export default function ResourceSelection({
  selection_version: version,
  selection_required: required,
  plan_key: plan,
  plans,
  members,
  locations,
  jobs,
}: Props) {
  const { t } = useTranslation();
  const formRef = useRef<FormComponentRef>(null);
  const confirmed = useRef(false);
  const [review, setReview] = useState<{
    staff: string[];
    locations: string[];
    plan: string;
    handoffs: {
      id: string;
      assignee: string;
      billing: string;
      helpers: string[];
    }[];
  } | null>(null);
  function reviewSelection() {
    const data = formRef.current?.getFormData();
    if (data)
      setReview({
        plan: String(data.get("resource_selection[plan_key]") || plan),
        handoffs: jobs.map((job) => ({
          id: job.id,
          assignee: String(
            data.get(`resource_selection[handoffs][${job.id}][assignee_id]`) ||
              "",
          ),
          billing: String(
            data.get(
              `resource_selection[handoffs][${job.id}][billing_assignee_id]`,
            ) || "",
          ),
          helpers: data
            .getAll(`resource_selection[handoffs][${job.id}][helper_ids][]`)
            .map(String),
        })),
        staff: data.getAll("resource_selection[membership_ids][]").map(String),
        locations: data
          .getAll("resource_selection[location_ids][]")
          .map(String),
      });
  }
  const selectedPlan = plans.find((item) => item.key === review?.plan);
  const person = (id: string | null) =>
    members.find((member) => member.user_id === id)?.name ||
    t("resources.choose");
  const active = members.filter((member) => member.active);
  return (
    <SettingsLayout>
      <Head title={t("resources.title")} />
      <div className="space-y-6">
        <header className="space-y-2">
          <h1 className="text-page font-display">{t("resources.title")}</h1>
          <p className="text-muted-foreground text-sm">
            {t("resources.description")}
          </p>
        </header>
        {required && (
          <div role="alert" className="rounded-lg border p-4">
            <p className="font-medium">{t("resources.paused")}</p>
            <p className="mt-2 text-sm">{t("resources.resolve_or_upgrade")}</p>
            <Link className="mt-2 inline-block underline" href="/user/billing">
              {t("common.billing")}
            </Link>
          </div>
        )}
        <Form
          ref={formRef}
          id="resource-selection-form"
          onBefore={() => {
            if (!confirmed.current) {
              reviewSelection();
              return false;
            }
          }}
          onFinish={() => {
            confirmed.current = false;
            setReview(null);
          }}
          action="/user/resource_selection"
          method="patch"
          options={{ preserveScroll: true }}
        >
          {({ errors, processing }) => (
            <div className="space-y-6">
              <input
                type="hidden"
                name="resource_selection[expected_version]"
                value={version}
              />
              {Object.keys(errors).length > 0 && (
                <div
                  role="alert"
                  className="rounded-lg border border-destructive p-4"
                >
                  <FieldError>
                    {Object.values(errors).flat().join(" ")}
                  </FieldError>
                </div>
              )}
              <Field>
                <FieldLabel htmlFor="resource-plan">
                  {t("resources.plan")}
                </FieldLabel>
                <Choice
                  id="resource-plan"
                  name="resource_selection[plan_key]"
                  value={plan}
                  choices={plans.map((item) => ({
                    value: item.key,
                    label: `${item.name} · ${t("resources.limits", { staff: item.limits.users, locations: item.limits.locations ?? t("resources.unlimited") })}`,
                  }))}
                />
                <p className="text-muted-foreground text-sm">
                  {t("resources.no_subscription_change")}
                </p>
              </Field>
              <Card>
                <CardHeader>
                  <CardTitle>{t("resources.staff")}</CardTitle>
                  <CardDescription>{t("resources.staff_help")}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {active.map((member) => (
                    <Field key={member.id} orientation="horizontal">
                      <Checkbox
                        id={`retain-member-${member.id}`}
                        name="resource_selection[membership_ids][]"
                        value={member.id}
                        defaultChecked
                        disabled={member.self}
                      />
                      {member.self && (
                        <input
                          type="hidden"
                          name="resource_selection[membership_ids][]"
                          value={member.id}
                        />
                      )}
                      <FieldLabel
                        htmlFor={`retain-member-${member.id}`}
                        className="min-w-0 break-words"
                      >
                        {member.name}{" "}
                        <Badge variant="secondary">
                          {t(`team.${member.role}`)}
                        </Badge>
                      </FieldLabel>
                    </Field>
                  ))}
                </CardContent>
              </Card>
              <Card>
                <CardHeader>
                  <CardTitle>{t("resources.locations")}</CardTitle>
                  <CardDescription>
                    {t("resources.locations_help")}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {locations
                    .filter((location) => location.active)
                    .map((location) => (
                      <Field key={location.id} orientation="horizontal">
                        <Checkbox
                          id={`retain-location-${location.id}`}
                          name="resource_selection[location_ids][]"
                          value={location.id}
                          defaultChecked
                        />
                        <FieldLabel
                          htmlFor={`retain-location-${location.id}`}
                          className="min-w-0 break-words"
                        >
                          {location.name}{" "}
                          {location.unresolved && (
                            <Badge variant="outline">
                              {t("resources.unresolved")}
                            </Badge>
                          )}
                        </FieldLabel>
                      </Field>
                    ))}
                </CardContent>
              </Card>
              {jobs.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle>{t("resources.handoffs")}</CardTitle>
                    <CardDescription>
                      {t("resources.handoffs_help")}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {jobs.map((job) => {
                      const eligible = active.filter(
                        (member) =>
                          member.role === "admin" ||
                          member.location_ids.includes(job.location_id),
                      );
                      const name = `resource_selection[handoffs][${job.id}]`;
                      return (
                        <details key={job.id} className="rounded-lg border p-4">
                          <summary className="cursor-pointer break-words font-medium">
                            {job.customer_name} ·{" "}
                            {
                              locations.find(
                                (location) => location.id === job.location_id,
                              )?.name
                            }
                          </summary>
                          <div className="mt-4 space-y-4">
                            <p className="text-muted-foreground text-sm">
                              {t("resources.current_assignment", {
                                name:
                                  active.find(
                                    (member) =>
                                      member.user_id === job.assignee_id,
                                  )?.name ?? t("resources.inactive"),
                              })}
                            </p>
                            <input
                              type="hidden"
                              name={`${name}[expected_revision]`}
                              value={job.revision}
                            />
                            <Field>
                              <FieldLabel htmlFor={`operation-${job.id}`}>
                                {t("resources.operational_replacement")}
                              </FieldLabel>
                              <Choice
                                id={`operation-${job.id}`}
                                name={`${name}[assignee_id]`}
                                value=""
                                choices={[
                                  { value: "", label: t("resources.choose") },
                                  ...eligible.map((member) => ({
                                    value: member.user_id,
                                    label: member.name,
                                  })),
                                ]}
                              />
                            </Field>
                            <Field>
                              <FieldLabel htmlFor={`billing-${job.id}`}>
                                {t("resources.billing_replacement")}
                              </FieldLabel>
                              <Choice
                                id={`billing-${job.id}`}
                                name={`${name}[billing_assignee_id]`}
                                value=""
                                choices={[
                                  { value: "", label: t("resources.choose") },
                                  ...eligible
                                    .filter(
                                      (member) => member.role !== "member",
                                    )
                                    .map((member) => ({
                                      value: member.user_id,
                                      label: member.name,
                                    })),
                                ]}
                              />
                            </Field>
                            <fieldset className="space-y-3">
                              <legend className="mb-2 text-sm font-medium">
                                {t("resources.helpers")}
                              </legend>
                              {eligible.map((member) => (
                                <Field key={member.id} orientation="horizontal">
                                  <Checkbox
                                    id={`helper-${job.id}-${member.id}`}
                                    name={`${name}[helper_ids][]`}
                                    value={member.user_id}
                                  />
                                  <FieldLabel
                                    htmlFor={`helper-${job.id}-${member.id}`}
                                  >
                                    {member.name}
                                  </FieldLabel>
                                </Field>
                              ))}
                            </fieldset>
                          </div>
                        </details>
                      );
                    })}
                  </CardContent>
                </Card>
              )}
              <Button type="submit" disabled={processing}>
                {t("access.review_changes")}
              </Button>
              <Sheet
                open={!!review}
                onOpenChange={(open) => {
                  if (!open) setReview(null);
                }}
              >
                <SheetContent className="w-full! overflow-y-auto p-6 sm:max-w-xl!">
                  <SheetHeader className="p-0">
                    <SheetTitle>{t("access.resource_review")}</SheetTitle>
                    <SheetDescription>
                      {t("access.resource_impact")}
                    </SheetDescription>
                  </SheetHeader>
                  {selectedPlan && (
                    <section className="mt-6 space-y-2 rounded-lg border p-4">
                      <h3 className="font-semibold">{selectedPlan.name}</h3>
                      <p>
                        {t("resources.limits", {
                          staff: selectedPlan.limits.users,
                          locations:
                            selectedPlan.limits.locations ??
                            t("resources.unlimited"),
                        })}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {t("resources.no_subscription_change")}
                      </p>
                    </section>
                  )}
                  <p className="mt-6 font-medium">
                    {t("access.retained_count", {
                      staff: review?.staff.length ?? 0,
                      locations: review?.locations.length ?? 0,
                    })}
                  </p>
                  <ul className="mt-4 list-inside list-disc break-words">
                    {members
                      .filter((member) => review?.staff.includes(member.id))
                      .map((member) => (
                        <li key={member.id}>{member.name}</li>
                      ))}
                    {locations
                      .filter((location) =>
                        review?.locations.includes(location.id),
                      )
                      .map((location) => (
                        <li key={location.id}>{location.name}</li>
                      ))}
                  </ul>
                  {review?.handoffs
                    .filter(
                      (handoff) =>
                        handoff.assignee ||
                        handoff.billing ||
                        handoff.helpers.length,
                    )
                    .map((handoff) => {
                      const job = jobs.find((item) => item.id === handoff.id)!;
                      return (
                        <section
                          key={handoff.id}
                          className="mt-4 space-y-2 rounded-lg border p-4"
                        >
                          <h3 className="font-semibold">{job.customer_name}</h3>
                          <dl className="space-y-3 text-sm">
                            <div>
                              <dt className="text-muted-foreground">
                                {t("resources.operational_replacement")}
                              </dt>
                              <dd>
                                {person(job.assignee_id)} →{" "}
                                {person(handoff.assignee)}
                              </dd>
                            </div>
                            <div>
                              <dt className="text-muted-foreground">
                                {t("resources.billing_replacement")}
                              </dt>
                              <dd>
                                {person(job.billing_assignee_id)} →{" "}
                                {person(handoff.billing)}
                              </dd>
                            </div>
                            <div>
                              <dt className="text-muted-foreground">
                                {t("resources.helpers")}
                              </dt>
                              <dd>
                                {job.helper_ids.map(person).join(", ") || "—"} →{" "}
                                {handoff.helpers.map(person).join(", ") || "—"}
                              </dd>
                            </div>
                          </dl>
                        </section>
                      );
                    })}
                  <p className="my-6 text-sm text-muted-foreground">
                    {t("access.no_change_yet")}
                  </p>
                  <div className="flex flex-wrap gap-3">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setReview(null)}
                    >
                      {t("common.back")}
                    </Button>
                    <Button
                      type="button"
                      disabled={processing}
                      onClick={() => {
                        confirmed.current = true;
                        formRef.current?.submit();
                      }}
                    >
                      {t("resources.save")}
                    </Button>
                  </div>
                </SheetContent>
              </Sheet>
            </div>
          )}
        </Form>
        <Card>
          <CardHeader>
            <CardTitle>{t("resources.retained_history")}</CardTitle>
            <CardDescription>{t("resources.history_help")}</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-wrap gap-3">
            {locations.map((location) => (
              <Button
                key={location.id}
                variant="outline"
                nativeButton={false}
                render={
                  <Link href={`/user/location_histories/${location.id}`} />
                }
              >
                {location.name}
                {!location.active && ` · ${t("resources.inactive")}`}
              </Button>
            ))}
          </CardContent>
        </Card>
        {locations
          .filter((location) => !location.active)
          .map((location) => (
            <Form
              key={location.id}
              action={`/user/locations/${location.id}/activation`}
              method="post"
            >
              {({ processing }) => (
                <Button type="submit" variant="outline" disabled={processing}>
                  {t("resources.reactivate", { name: location.name })}
                </Button>
              )}
            </Form>
          ))}
      </div>
    </SettingsLayout>
  );
}
